import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable, Column } from '@/components/ui/data-table';
import {
  fxRatesService,
  FXRate,
  FXRateCreate,
  FXConversion,
} from '@/services/fx-rates.service';
import { toast } from 'react-hot-toast';
import {
  Plus,
  ArrowRightLeft,
  Calculator,
  Save,
  X,
  Edit,
  TrendingUp,
  DollarSign,
} from 'lucide-react';

interface FXRateDisplay extends FXRate {
  id?: number;
}

const FXRatesPage: React.FC = () => {
  const [fxRates, setFXRates] = useState<FXRateDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showConverter, setShowConverter] = useState(false);
  const [editingRate, setEditingRate] = useState<FXRateDisplay | null>(null);

  // Form state for adding new rates
  const [formData, setFormData] = useState<FXRateCreate>({
    from_currency: 'USD',
    to_currency: 'EUR',
    rate_date: new Date().toISOString().split('T')[0],
    exchange_rate: 0,
  });

  // Converter state
  const [converterData, setConverterData] = useState({
    amount: 1000000,
    fromCurrency: 'USD',
    toCurrency: 'EUR',
  });
  const [conversionResult, setConversionResult] = useState<FXConversion | null>(
    null
  );
  const [converting, setConverting] = useState(false);

  const currencies = ['USD', 'EUR', 'GBP'];

  useEffect(() => {
    loadFXRates();
  }, []);

  const loadFXRates = async () => {
    try {
      setLoading(true);
      const data = await fxRatesService.getFXRates();

      // Transform data to include ID for display
      const transformedData: FXRateDisplay[] = data.map((rate, index) => ({
        ...rate,
        id: index,
      }));

      setFXRates(transformedData);
    } catch (error) {
      console.error('Error loading FX rates:', error);
      toast.error('Failed to load FX rates');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRate = async () => {
    try {
      await fxRatesService.updateFXRates([formData]);
      toast.success('FX rate added successfully');
      setShowAddForm(false);
      setFormData({
        from_currency: 'USD',
        to_currency: 'EUR',
        rate_date: new Date().toISOString().split('T')[0],
        exchange_rate: 0,
      });
      loadFXRates();
    } catch (error) {
      console.error('Error adding FX rate:', error);
      toast.error('Failed to add FX rate');
    }
  };

  const handleUpdateRates = async () => {
    try {
      const ratesToUpdate = fxRates.map((rate) => ({
        from_currency: rate.from_currency,
        to_currency: rate.to_currency,
        rate_date: rate.rate_date,
        exchange_rate: rate.exchange_rate,
      }));
      await fxRatesService.updateFXRates(ratesToUpdate);
      toast.success('FX rates updated successfully');
      setEditingRate(null);
      loadFXRates();
    } catch (error) {
      console.error('Error updating FX rates:', error);
      toast.error('Failed to update FX rates');
    }
  };

  const handleConvert = async () => {
    try {
      setConverting(true);
      const result = await fxRatesService.convertCurrency(
        converterData.amount,
        converterData.fromCurrency,
        converterData.toCurrency
      );
      setConversionResult(result);
    } catch (error) {
      console.error('Error converting currency:', error);
      toast.error('Failed to convert currency');
    } finally {
      setConverting(false);
    }
  };

  const handleEditRate = (rate: FXRateDisplay) => {
    setEditingRate(rate);
  };

  const handleSaveEdit = () => {
    if (editingRate) {
      const updatedRates = fxRates.map((rate) =>
        rate.id === editingRate.id ? editingRate : rate
      );
      setFXRates(updatedRates);
      handleUpdateRates();
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatRate = (rate: number) => {
    return rate.toFixed(6);
  };

  const columns: Column<FXRateDisplay>[] = [
    {
      header: 'From',
      accessor: 'from_currency',
    },
    {
      header: 'To',
      accessor: 'to_currency',
    },
    {
      header: 'Rate Date',
      accessor: (row) => new Date(row.rate_date).toLocaleDateString(),
    },
    {
      header: 'Exchange Rate',
      accessor: (row) => {
        if (editingRate?.id === row.id) {
          return (
            <Input
              type="number"
              step="0.000001"
              value={editingRate?.exchange_rate || 0}
              onChange={(e) =>
                editingRate &&
                setEditingRate({
                  ...editingRate,
                  exchange_rate: parseFloat(e.target.value) || 0,
                })
              }
              className="w-32"
              data-test-id="edit-rate-input"
            />
          );
        }
        return formatRate(row.exchange_rate);
      },
    },
    {
      header: 'Actions',
      accessor: (row) => {
        if (editingRate?.id === row.id) {
          return (
            <div className="flex gap-2" data-test-id="edit-actions">
              <button
                onClick={handleSaveEdit}
                className="text-green-600 hover:text-green-900"
                data-test-id="save-edit-button"
              >
                <Save className="h-4 w-4" data-test-id="save-edit-icon" />
              </button>
              <button
                onClick={() => setEditingRate(null)}
                className="text-red-600 hover:text-red-900"
                data-test-id="cancel-edit-button"
              >
                <X className="h-4 w-4" data-test-id="cancel-edit-icon" />
              </button>
            </div>
          );
        }
        return (
          <button
            onClick={() => handleEditRate(row)}
            className="text-blue-600 hover:text-blue-900"
            data-test-id="edit-rate-button"
          >
            <Edit className="h-4 w-4" data-test-id="edit-rate-icon" />
          </button>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="page-container" data-test-id="fx-rates-page-container">
        <div className="page-inner" data-test-id="fx-rates-page-inner">
          <div
            className="flex items-center justify-center h-64"
            data-test-id="loading-wrapper"
          >
            <div
              className="text-lg text-gray-600"
              data-test-id="loading-fx-rates"
            >
              Loading FX rates...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-test-id="fx-rates-page-container">
      <div className="page-inner" data-test-id="fx-rates-page-inner">
        {/* Header */}
        <div className="mb-8" data-test-id="header-section">
          <div
            className="flex justify-between items-center"
            data-test-id="header-wrapper"
          >
            <div data-test-id="header-content">
              <h1 className="section-title mb-2" data-test-id="page-title">
                FX Rates Management
              </h1>
              <p className="section-subtitle" data-test-id="page-subtitle">
                Manage foreign exchange rates and currency conversions
              </p>
            </div>
            <div className="flex gap-2" data-test-id="header-actions">
              <Button
                variant="outline"
                onClick={() => setShowConverter(!showConverter)}
                className="flex items-center gap-2"
                data-test-id="currency-converter-button"
              >
                <Calculator
                  className="h-4 w-4"
                  data-test-id="calculator-icon"
                />
                Currency Converter
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center gap-2"
                data-test-id="add-rate-button"
              >
                <Plus className="h-4 w-4" data-test-id="add-rate-icon" />
                Add Rate
              </Button>
            </div>
          </div>
        </div>

        {/* Currency Converter */}
        {showConverter && (
          <Card className="p-6 mb-6" data-test-id="converter-card">
            <h3
              className="text-lg font-semibold mb-4"
              data-test-id="converter-title"
            >
              Currency Converter
            </h3>
            <div
              className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end"
              data-test-id="converter-form"
            >
              <div data-test-id="amount-field">
                <Label htmlFor="amount" data-test-id="amount-label">
                  Amount
                </Label>
                <Input
                  id="amount"
                  type="number"
                  value={converterData.amount}
                  onChange={(e) =>
                    setConverterData({
                      ...converterData,
                      amount: parseFloat(e.target.value) || 0,
                    })
                  }
                  data-test-id="amount-input"
                />
              </div>
              <div data-test-id="from-currency-field">
                <Label
                  htmlFor="from_currency"
                  data-test-id="from-currency-label"
                >
                  From Currency
                </Label>
                <select
                  id="from_currency"
                  className="w-full px-3 py-2 border rounded-md"
                  value={converterData.fromCurrency}
                  onChange={(e) =>
                    setConverterData({
                      ...converterData,
                      fromCurrency: e.target.value,
                    })
                  }
                  data-test-id="from-currency-select"
                >
                  {currencies.map((currency) => (
                    <option
                      key={currency}
                      value={currency}
                      data-test-id={`from-currency-option-${currency}`}
                    >
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
              <div data-test-id="to-currency-field">
                <Label htmlFor="to_currency" data-test-id="to-currency-label">
                  To Currency
                </Label>
                <select
                  id="to_currency"
                  className="w-full px-3 py-2 border rounded-md"
                  value={converterData.toCurrency}
                  onChange={(e) =>
                    setConverterData({
                      ...converterData,
                      toCurrency: e.target.value,
                    })
                  }
                  data-test-id="to-currency-select"
                >
                  {currencies.map((currency) => (
                    <option
                      key={currency}
                      value={currency}
                      data-test-id={`to-currency-option-${currency}`}
                    >
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={handleConvert}
                disabled={converting}
                className="flex items-center gap-2"
                data-test-id="convert-button"
              >
                <ArrowRightLeft
                  className="h-4 w-4"
                  data-test-id="convert-icon"
                />
                {converting ? 'Converting...' : 'Convert'}
              </Button>
            </div>

            {conversionResult && (
              <div
                className="mt-4 p-4 bg-gray-50 rounded-lg"
                data-test-id="conversion-result"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div data-test-id="original-amount">
                    <p className="text-sm text-gray-600">Original Amount</p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(
                        converterData.amount,
                        conversionResult.from_currency
                      )}
                    </p>
                  </div>
                  <div data-test-id="exchange-rate">
                    <p className="text-sm text-gray-600">Exchange Rate</p>
                    <p className="text-lg font-semibold">
                      {formatRate(conversionResult.exchange_rate)}
                    </p>
                  </div>
                  <div data-test-id="converted-amount">
                    <p className="text-sm text-gray-600">Converted Amount</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(
                        conversionResult.converted_amount,
                        conversionResult.to_currency
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Add Rate Form */}
        {showAddForm && (
          <Card className="p-6 mb-6" data-test-id="add-form-card">
            <h3
              className="text-lg font-semibold mb-4"
              data-test-id="add-form-title"
            >
              Add New FX Rate
            </h3>
            <div
              className="grid grid-cols-1 md:grid-cols-4 gap-4"
              data-test-id="add-form-grid"
            >
              <div data-test-id="from-currency-field">
                <Label
                  htmlFor="form_from_currency"
                  data-test-id="form-from-currency-label"
                >
                  From Currency
                </Label>
                <select
                  id="form_from_currency"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.from_currency}
                  onChange={(e) =>
                    setFormData({ ...formData, from_currency: e.target.value })
                  }
                  data-test-id="form-from-currency-select"
                >
                  {currencies.map((currency) => (
                    <option
                      key={currency}
                      value={currency}
                      data-test-id={`form-from-currency-option-${currency}`}
                    >
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
              <div data-test-id="to-currency-field">
                <Label
                  htmlFor="form_to_currency"
                  data-test-id="form-to-currency-label"
                >
                  To Currency
                </Label>
                <select
                  id="form_to_currency"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.to_currency}
                  onChange={(e) =>
                    setFormData({ ...formData, to_currency: e.target.value })
                  }
                  data-test-id="form-to-currency-select"
                >
                  {currencies.map((currency) => (
                    <option
                      key={currency}
                      value={currency}
                      data-test-id={`form-to-currency-option-${currency}`}
                    >
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
              <div data-test-id="rate-date-field">
                <Label htmlFor="rate_date" data-test-id="rate-date-label">
                  Rate Date
                </Label>
                <Input
                  id="rate_date"
                  type="date"
                  value={formData.rate_date}
                  onChange={(e) =>
                    setFormData({ ...formData, rate_date: e.target.value })
                  }
                  data-test-id="rate-date-input"
                />
              </div>
              <div data-test-id="exchange-rate-field">
                <Label
                  htmlFor="exchange_rate"
                  data-test-id="exchange-rate-label"
                >
                  Exchange Rate
                </Label>
                <Input
                  id="exchange_rate"
                  type="number"
                  step="0.000001"
                  value={formData.exchange_rate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      exchange_rate: parseFloat(e.target.value) || 0,
                    })
                  }
                  data-test-id="exchange-rate-input"
                />
              </div>
            </div>
            <div
              className="flex justify-end gap-2 mt-4"
              data-test-id="add-form-actions"
            >
              <Button
                variant="outline"
                onClick={() => setShowAddForm(false)}
                data-test-id="cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddRate}
                data-test-id="add-rate-submit-button"
              >
                Add Rate
              </Button>
            </div>
          </Card>
        )}

        {/* FX Rates Table */}
        <Card className="p-6" data-test-id="rates-table-card">
          <h3
            className="text-lg font-semibold mb-4"
            data-test-id="rates-table-title"
          >
            Current FX Rates ({fxRates.length} rates)
          </h3>
          <DataTable
            columns={columns}
            data={fxRates}
            keyExtractor={(row) =>
              `${row.from_currency}-${row.to_currency}-${row.id}`
            }
            emptyMessage="No FX rates found. Click 'Add Rate' to create one."
            data-test-id="fx-rates-table"
          />
        </Card>

        {/* Summary Statistics */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8"
          data-test-id="summary-statistics"
        >
          <Card className="p-6" data-test-id="total-rates-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="total-rates-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="total-rates-title"
              >
                Total Rates
              </h3>
              <DollarSign
                className="h-5 w-5 text-green-600"
                data-test-id="total-rates-icon"
              />
            </div>
            <p className="text-2xl font-bold" data-test-id="total-rates-count">
              {fxRates.length}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="total-rates-label"
            >
              Active exchange rates
            </p>
          </Card>
          <Card className="p-6" data-test-id="currency-pairs-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="currency-pairs-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="currency-pairs-title"
              >
                Currency Pairs
              </h3>
              <TrendingUp
                className="h-5 w-5 text-blue-600"
                data-test-id="currency-pairs-icon"
              />
            </div>
            <p
              className="text-2xl font-bold"
              data-test-id="currency-pairs-count"
            >
              {
                new Set(
                  fxRates.map(
                    (rate) => `${rate.from_currency}-${rate.to_currency}`
                  )
                ).size
              }
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="currency-pairs-label"
            >
              Unique pairs
            </p>
          </Card>
          <Card className="p-6" data-test-id="last-updated-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="last-updated-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="last-updated-title"
              >
                Last Updated
              </h3>
              <ArrowRightLeft
                className="h-5 w-5 text-purple-600"
                data-test-id="last-updated-icon"
              />
            </div>
            <p className="text-sm font-medium" data-test-id="last-updated-date">
              {new Date().toLocaleDateString()}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="last-updated-label"
            >
              As of today
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FXRatesPage;
